import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { peopleSelectControlSx, peopleSelectSx } from "../../People/peopleSelectStyles";
import { teamGroupSelectMenuProps } from "../../People/Teams/teamFormStyles";
import { fieldErrorSx, peopleFormErrorTextSx } from "../../People/peopleFormValidation";

const fieldLabelSx = {
  color: "#405261",
  fontFamily: "Inter",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "normal",
  mb: 0.75,
};

const singleLineInputSx = {
  width: 360,
  maxWidth: "100%",
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "8px",
    background: "#FFF",
    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
    fontFamily: "Inter",
    fontSize: 14,
    "& fieldset": { border: "1px solid #D5DCE1" },
    "&:hover fieldset": { border: "1px solid #D5DCE1" },
    "&.Mui-focused fieldset": { border: "1px solid #D5DCE1" },
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
    px: 1.5,
    boxSizing: "border-box",
    height: 40,
    "&::placeholder": { color: "#C5CED6", opacity: 1 },
  },
};

const selectSx = peopleSelectControlSx({ width: 360, maxWidth: "100%" });

const TAG_TYPE_OPTIONS = [
  { value: "conversation", label: "Conversation Tags" },
  { value: "knowledge_base", label: "Knowledge Base Tags" },
];

const EMPTY_FORM = { name: "", tagType: "conversation" };
const EMPTY_ERRORS = { name: "", tagType: "" };

export default function TagCreateModal({ open, onClose, onConfirm, submitting = false }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);

  const selectedTypeLabel =
    TAG_TYPE_OPTIONS.find((opt) => opt.value === form.tagType)?.label ||
    "Conversation Tags";

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setErrors(EMPTY_ERRORS);
    }
  }, [open]);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const next = { name: "", tagType: "" };
    let valid = true;

    if (!form.name.trim()) {
      next.name = "Tag name is required.";
      valid = false;
    }

    if (!form.tagType) {
      next.tagType = "Tag type is required.";
      valid = false;
    }

    setErrors(next);
    return valid;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onConfirm({
      name: form.name.trim(),
      tagType: form.tagType,
    });
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setErrors(EMPTY_ERRORS);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: 408,
          maxWidth: "95vw",
          borderRadius: "12px",
          border: "1px solid #D5DCE1",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          m: 2,
        },
      }}
    >
      <Box sx={{ p: "24px", position: "relative" }}>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", top: 16, right: 16, p: 0.5 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter",
            fontSize: 18,
            fontWeight: 500,
            lineHeight: "18px",
            mb: 2.5,
          }}
        >
          Create New Tag
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={fieldLabelSx}>Tag Name</Typography>
            <TextField
              fullWidth
              placeholder="e.g, email, website"
              value={form.name}
              onChange={set("name")}
              error={Boolean(errors.name)}
              sx={{ ...singleLineInputSx, ...fieldErrorSx(errors.name) }}
            />
            {errors.name ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.name}</Typography>
            ) : null}
          </Box>

          <Box>
            <Typography sx={fieldLabelSx}>Tag Type</Typography>
            <FormControl
              size="small"
              error={Boolean(errors.tagType)}
              sx={{ ...selectSx, ...fieldErrorSx(errors.tagType) }}
            >
              <Select
                value={form.tagType}
                onChange={set("tagType")}
                IconComponent={Dropdown}
                MenuProps={teamGroupSelectMenuProps()}
                sx={peopleSelectSx}
                renderValue={() => (
                  <Typography
                    component="span"
                    noWrap
                    sx={{
                      color: "#061522",
                      fontSize: 14,
                      fontFamily: "Inter",
                      fontWeight: 400,
                    }}
                  >
                    {selectedTypeLabel}
                  </Typography>
                )}
              >
                {TAG_TYPE_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    sx={{ fontFamily: "Inter", fontSize: 14 }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.tagType ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.tagType}</Typography>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
          <Button
            onClick={handleClose}
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              border: "1px solid #D5DCE1",
              background: "#FFF",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#405261",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant="contained"
            disableElevation
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              bgcolor: "#148DFD",
              backgroundColor: "#148DFD",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#FFF",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              "&:hover": {
                bgcolor: "#127EDE",
                backgroundColor: "#127EDE",
                boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              },
              "&.Mui-disabled": {
                color: "#93A1B0",
                bgcolor: "#F9FAFA",
                backgroundColor: "#F9FAFA",
                border: "1px solid #C5CED6",
              },
            }}
          >
            {submitting ? "Creating…" : "Create Tag"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
